import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  KeyboardEvent
} from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuidv4 } from 'uuid'

import {
  SEARCH_ENGINE_ICONS,
  IconType,
  getSearchIconByType
} from '@/constants/searchEngineAssets'

import CommonInput from '../form/Input/CommonInput'
import CommonButton from '../form/Button/CommonButton'
import BtnGlobalCustomStyles from '../form/Button/button.customStyles'

import {
  DropdownContainer,
  DropdownList,
  ListItem,
  NoOptionsMessage,
  RecentSearchesWrapper,
  RecentSearchesTitle,
  SearchItemsWrapper,
  SearchItem,
  Divider
} from './Dropdown.styled'

interface SearchResult {
  id: string
  label: string
  value: [number, number]
  isAirport: boolean
  isRecentSearch?: boolean
  isParking?: boolean
  metadata?: {
    type: string
    country: string
    relevance: number
  }
}

const MAX_RECENT_SEARCHES = 5
const RECENT_SEARCHES_KEY = 'recentSearches'

type Option = {
  id: number
  label: string
}

type DropdownProps = {
  'options': SearchResult[]
  'fetchMoreOptions'?: () => void
  'inputValue': string
  'setInputValue': (value: string) => void
  'noOptionsMessage': string | null
  'placeholder'?: string
  'field'?: {
    value: string
    onChange: (value: SearchResult) => void
  }
  'onKeyDown'?: (event: KeyboardEvent<HTMLInputElement>) => void
  'hideListWhileTyping'?: boolean
  'data-test-id'?: string
  'onSelectOption'?: (option: SearchResult) => void
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  fetchMoreOptions,
  inputValue,
  setInputValue,
  noOptionsMessage,
  placeholder,
  field,
  onKeyDown,
  hideListWhileTyping = false,
  'data-test-id': dataTestId,
  onSelectOption
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('parkings')

  const handleSelect = (option: SearchResult) => {
    setInputValue(option.label)

    if (field) {
      field.onChange(option)
    }

    if (onSelectOption) {
      onSelectOption(option)
    }

    setIsOpen(false)
    saveToRecentSearches(option)
  }

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    const savedSearches = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches))
      } catch (error) {
        console.error('Error parsing saved searches:', error)
      }
    }
  }, [])

  useEffect(() => {
    if (inputValue.length > 0) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      setIsTyping(true)
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
      }, 500)
    } else {
      setIsTyping(false)
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [inputValue])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollTop + clientHeight >= scrollHeight) {
      fetchMoreOptions && fetchMoreOptions()
    }
  }

  const saveToRecentSearches = (item: SearchResult) => {
    const newItem = {
      ...item,
      id: uuidv4(),
      isRecentSearch: true
    }

    let updatedSearches = [newItem, ...recentSearches]

    updatedSearches = updatedSearches.filter(
      (search, index, self) =>
        index === self.findIndex(s => s.label === search.label)
    )

    if (updatedSearches.length > MAX_RECENT_SEARCHES) {
      updatedSearches = updatedSearches.slice(0, MAX_RECENT_SEARCHES)
    }

    setRecentSearches(updatedSearches)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updatedSearches))
  }

  const removeRecentSearch = (id: string, label: string) => {
    const updatedSearches = recentSearches.filter(item => item.id !== id)
    setRecentSearches(updatedSearches)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updatedSearches))

    if (inputValue === label) {
      setInputValue('')
    }
  }

  const highlightMatch = (text: string, highlight: string) => {
    if (!highlight) return text

    const regex = new RegExp(`(${highlight})`, 'i')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? <strong key={index}>{part}</strong> : part
    )
  }

  const historyIsRelatedToInputValue = recentSearches.some(
    (item: SearchResult) => {
      return item.label.toLowerCase().includes(inputValue.toLowerCase())
    }
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    if (!isOpen) setIsOpen(true)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsOpen(false)
    }

    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const shouldShowDropdown =
    isOpen &&
    (options.length > 0 ||
      (recentSearches.length > 0 && historyIsRelatedToInputValue)) &&
    !(hideListWhileTyping && isTyping)

  return (
    <DropdownContainer ref={dropdownRef} data-test-id={dataTestId}>
      <CommonInput
        variant="default"
        type="text"
        value={inputValue}
        onClick={handleToggle}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        data-test-id="searchbox-input"
        ref={inputRef}
      />
      {shouldShowDropdown ? (
        <DropdownList
          onScroll={handleScroll}
          data-test-id="searchbox-suggestions-list"
        >
          {recentSearches.length > 0 && historyIsRelatedToInputValue && (
            <>
              <RecentSearchesWrapper>
                <RecentSearchesTitle>
                  {t('searchNearParking.search.recentSearches')}
                </RecentSearchesTitle>
                <CommonButton
                  variant="options"
                  onClick={() => {
                    setRecentSearches([])
                    localStorage.removeItem(RECENT_SEARCHES_KEY)
                  }}
                  size="S"
                  customStyle={BtnGlobalCustomStyles.searchClearHistory}
                >
                  {t('searchNearParking.search.clearHistory')}
                </CommonButton>
              </RecentSearchesWrapper>
              <SearchItemsWrapper>
                {recentSearches
                  .filter((item: SearchResult) =>
                    item.label.toLowerCase().includes(inputValue.toLowerCase())
                  )
                  .map((item: SearchResult, index: number) => (
                    <SearchItem key={index} onClick={() => handleSelect(item)}>
                      <img
                        src={getSearchIconByType(IconType.RECENT_SEARCH)}
                        alt="recent search icon"
                        width="20px"
                        height="20px"
                      />
                      <span>{highlightMatch(item.label, inputValue)}</span>
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          removeRecentSearch(item.id, item.label)
                        }}
                      >
                        <img
                          src={SEARCH_ENGINE_ICONS.deleteRecentSearch}
                          alt="delete icon"
                          width="16px"
                          height="16px"
                        />
                      </button>
                    </SearchItem>
                  ))}
              </SearchItemsWrapper>
              <Divider />
            </>
          )}
          {options.length > 0
            ? options.map((option, idx) => (
                <ListItem
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  data-test-id={`searchbox-item-${idx}`}
                >
                  <img
                    src={getSearchIconByType(IconType.LOCATION)}
                    alt="location icon"
                    width="20px"
                    height="20px"
                  />
                  <span>{highlightMatch(option.label, inputValue)}</span>
                </ListItem>
              ))
            : noOptionsMessage && (
                <NoOptionsMessage>{noOptionsMessage}</NoOptionsMessage>
              )}
        </DropdownList>
      ) : (
        <></>
      )}
    </DropdownContainer>
  )
}

export default Dropdown
